/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-2016 Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import moment from 'moment';
import { momentLocalizer } from 'react-big-calendar';
import Money from 'js-money';

import salesPayloadTransform from './configs/etl/sales/salesPayloadTransform';
import salesColumnsConfig from './configs/etl/sales/salesColumnsConfig';
import productsPayloadTransform from './configs/etl/products/productsPayloadTransform';
// import productsBrandTransform from './configs/etl/products/productsBrandTransform';
import masterProductsPayloadTransform from './configs/etl/masterproducts/masterProductsPayloadTransform';
import productsColumnsConfig from './configs/etl/products/productsColumnsConfig';
import repsColumnsConfig from './configs/etl/reps/repsColumnsConfig';
import repsPayloadTransform from './configs/etl/reps/repsPayloadTransform';
import salonsColumnsConfig from './configs/etl/salons/salonsColumnsConfig';
import salonsPayloadTransform from './configs/etl/salons/salonsPayloadTransform';
import masterProductsColumnsConfig from './configs/etl/masterproducts/masterProductsColumnsConfig';
import kpisColumnsConfig from './configs/etl/kpis/kpisColumnsConfig';

import groups from './configs/groups';
import allowedTags from './configs/allowedTags';
import statesByCountry from './configs/statesByCountry';
import siteConfigs from './configs/sites';

import cmdctrlSidebar from './configs/sidebar/cmdctrl';
import beautyerpSidebar from './configs/sidebar/beautyerp';

// import mytrackerSidebar from "./configs/sidebar/mytracker";
// import poeSidebar from "./configs/sidebar/poe";
import clubskpSidebar from './configs/sidebar/clubskp';
import clubskpLoggedInSidebar from './configs/sidebar/clubskpLoggedIn';
import clubskpLoggedInSalonSidebar from './configs/sidebar/clubskpLoggedInSalon';
import etlSidebar from './configs/sidebar/etl';
import filevaultSidebar from './configs/sidebar/filevault';
import messengerSidebar from './configs/sidebar/messenger';
import advisornetSidebar from './configs/sidebar/advisornet';
import salondbSidebar from './configs/sidebar/salondb';

import { normalizeDiacriticsAndCase } from './core/utils';

Money.addCurrencies({
  POINTS: {
    symbol: 'Points',
    label: 'Point',
    symbol_native: 'P',
    decimal_digits: 0,
    rounding: 0,
    code: 'POINTS',
    name_plural: 'Points',
  },
});

/* eslint-disable max-len */

// export const routerOptions = {
//   baseUrl: '',
// };

export const defaultLocale = 'en-CA';

// Setup the localizer by providing the moment (or globalize) Object
// to the correct localizer.
momentLocalizer(moment); // or globalizeLocalizer

export const sanitizeHtmlOptions = allowedTags;

export const modules = [
  {
    label: 'LOYALTY',
    value: 'loyalty',
    route: 'control-index',
  },
  { label: 'CMDCTRL', value: 'cmdctrl', route: 'control-index' },
  { label: 'ETL', value: 'etl', route: 'etl' },
  { label: 'FILEVAULT', value: 'filevault', route: 'filevault-index' },
  { label: 'POE', value: 'poe', route: 'poe-index' },
  {
    label: 'PRINTCENTER',
    value: 'printcenter',
    route: 'redirect',
    routeParams: { scope: 'printcenter' },
    // query:
    //   '?filters%5B0%5D%5Bkey%5D=data.category&filters%5B0%5D%5Boperator%5D=strIs&filters%5B0%5D%5Bvalue%5D=Print%20Center%20-%20Customizable',
  },
  {
    label: 'ADVISRONET',
    value: 'advisornet',
    route: userData => {
      const groupOptions = ((userData || {}).group || {}).options || [];
      return groupOptions.includes('user.advisornet.education') ||
        groupOptions.includes('user.manager.advisornet')
        ? 'advisornet-calendar'
        : 'advisornet-index';
    },
  },
  { label: 'MYTRACKER', value: 'mytracker', route: 'mytracker-index' },
  { label: 'MESSENGER', value: 'messenger', route: 'messenger-index' },
  { label: 'SALONDB', value: 'salondb', route: 'salondb-index' },
];

export const groupOptions = groups;

export const states = statesByCountry;

let isStagingOrDev = false;
if (process.env.BROWSER) {
  const hostname = (window.location || {}).hostname || '';
  isStagingOrDev = !!(
    hostname.includes('dev') || hostname.includes('localhost')
  );
} else {
  const mongoUri = (process.env || {}).MONGO_URI || '';
  isStagingOrDev = !!mongoUri.includes('dev');
}

// https://basecamp.com/2211488/projects/11325432/todos/248761859?enlarge=227206611#attachment_227206611
export const etl = {
  isStagingOrDev,
  fileContentTypes: [
    'text/comma-separated-values',
    'text/csv',
    'application/csv',
    'application/excel',
    'application/vnd.ms-excel',
    'application/vnd.msexcel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.google-apps.spreadsheet',
    'application/octet-stream',
  ],
  importTypes: {
    masterproducts: {
      label: 'Master Products',
      etlSteps: [
        {
          step: 'objectTransform',
          payload: {
            transform: (obj, ctx) => {
                     //eslint-disable-line
              const newObject = { ...obj };
              if (!newObject.$set) {
                newObject.$set = {};
              }
              newObject.$set.dm = new Date();

              if (newObject.$set.n) {
                newObject.$set.n_text = normalizeDiacriticsAndCase(
                  newObject.$set.n,
                );
              }

              if (!newObject.$setOnInsert) {
                newObject.$setOnInsert = {};
              }
              newObject.$setOnInsert.e = true;
              newObject.$setOnInsert.dc = new Date();

              // __tmp is used to store temporary data we may need
                     delete newObject.__tmp; //eslint-disable-line
              // console.dir(newObject, { depth: 999, color: true });
              return newObject;
            },
          },
        },
        {
          step: 'collect',
          payload: {
            count: 1000,
          },
        },
        {
          step: 'payloadTransform',
          payload: masterProductsPayloadTransform,
        },
        // {
        //   step: 'inspect',
        // },
        {
          step: 'mongoBulkUpdate',
          payload: {
            mapperName: 'masterProduct',
            updateKeys: ['$set.idh'],
            upsert: true,
          },
        },
      ],
      cols: masterProductsColumnsConfig,
    },
    // kpis: {
    //   label: 'KPIs',
    //   etlSteps: [
    //     {
    //       step: 'objectTransform',
    //       payload: {
    //         transform: (obj, ctx) => {
    //                  //eslint-disable-line
    //           const newObject = { ...obj };
    //           if (!newObject.$set) {
    //             newObject.$set = {};
    //           }
    //           newObject.$set.dm = new Date();

    //           if (!newObject.$setOnInsert) {
    //             newObject.$setOnInsert = {};
    //           }
    //           newObject.$setOnInsert.e = true;
    //           newObject.$setOnInsert.dc = new Date();

    //           // __tmp is used to store temporary data we may need
    //                  delete newObject.__tmp; //eslint-disable-line
    //           // console.dir(newObject, { depth: 999, color: true });
    //           return newObject;
    //         },
    //       },
    //     },
    //     {
    //       step: 'collect',
    //       payload: {
    //         count: 1000,
    //       },
    //     },
    //     {
    //       step: 'mongoBulkUpdate',
    //       payload: {
    //         mapperName: 'kpi',
    //         updateKeys: [
    //           '$set.targetYear',
    //           '$set.distributor',
    //           '$set.recordType',
    //           '$set.recordValue',
    //         ],
    //         upsert: true,
    //       },
    //     },
    //   ],
    //   cols: kpisColumnsConfig,
    // },
    products: {
      label: 'Products Import',
      etlSteps: [
        {
          step: 'collect',
          payload: {
            count: 1000,
          },
        },
        {
          step: 'payloadTransform',
          payload: productsPayloadTransform,
        },
        // {
        //   step: 'payloadTransform',
        //   payload: productsBrandTransform,
        // },
        // add brand columns
        {
          step: 'mongoBulkUpdate',
          payload: {
            mapperName: 'distributorProduct',
            updateKeys: ['$set.distributor', '$set.ext_id'],
            upsert: true,
          },
        },
      ],
      cols: productsColumnsConfig,
    },
    reps: {
      label: 'Reps Import',
      etlSteps: [
        {
          step: 'payloadTransform',
          payload: repsPayloadTransform,
        },
        // {
        //   step: 'objectTransform',
        //   payload: {
        //     transform: (obj, { Types, siteId }) => {
        //       const newObject = { ...obj };
        //       if (!newObject.$set) {
        //         newObject.$set = {};
        //       }
        //       newObject.$set.distributor = Types.ObjectId(newObject.__tmp.dist_id);   //eslint-disable-line
        //       newObject.$set.dm = new Date();
        //
        //       const nameChunks = newObject.__tmp.n.split(/\s+/);
        //       const nameArray = [nameChunks.shift(), nameChunks.join(' ')];
        //       newObject.$set.firstname = nameArray[0];
        //       newObject.$set.lastname = nameArray[1];
        //       newObject.$set.group = Types.ObjectId(
        //         siteConfigs[siteId].dscGroup,
        //       );
        //       newObject.$set.email = newObject.__tmp.email;
        //       newObject.$set.username = newObject.__tmp.email;
        //
        //       if (!newObject.$setOnInsert) {
        //         newObject.$setOnInsert = {};
        //       }
        //       newObject.$setOnInsert.e = true;
        //       newObject.$setOnInsert.dc = new Date();
        //
        //       // __tmp is used to store temporary data we may need
        //       delete newObject.__tmp;  //eslint-disable-line
        //       // console.dir(newObject, { depth: 999, color: true });
        //       return newObject;
        //     },
        //   },
        // },
        {
          step: 'collect',
          payload: {
            count: 1000,
          },
        },
        {
          step: 'mongoBulkUpdate',
          payload: {
            mapperName: 'user',
            updateKeys: ['$set.email'], // potential issue here since we have 2 unique ID's - email
            upsert: true,
          },
        },
      ],
      cols: repsColumnsConfig,
    },
    salons: {
      label: 'Salons Import',
      etlSteps: [
        {
          step: 'collect',
          payload: {
            count: 100,
          },
        },
        {
          step: 'payloadTransform',
          payload: salonsPayloadTransform,
        },
        {
          step: 'mongoBulkUpdate',
          payload: {
            mapperName: 'salon',
            updateKeys: ['$noop.ref.distributor', '$noop.ref.ext_id'],
            upsert: true,
          },
        },
        // TODO: add geolocation step
        // TODO: grab results and collect into batches of 50
      ],
      cols: salonsColumnsConfig,
    },
    sales: {
      label: 'Sales Import',
      etlSteps: [
        {
          step: 'collect',
          payload: {
            count: 1000,
          },
        },
        {
          step: 'payloadTransform',
          payload: salesPayloadTransform,
        },
        {
          step: 'mongoBulkUpdate',
          payload: {
            mapperName: 'distributorSale',
            updateKeys: [
              '$set.distributor',
              '$set.ext_salon_id',
              '$set.date',
              '$set.ext_id',
              '$set.ext_prod_id',
            ],
            upsert: true,
          },
        },
      ],
      cols: salesColumnsConfig,
    },
  },
};

export const colors = [
  '#d00f18',
  '#303030',
  '#888888',
  '#b6b6b6',
  '#c8c8c8',
  '#f8c5cb',
  '#f3a3ac',
];

// autoredirection based upon languages parameter below.
// will not try to negotiate site if languages parameter not set
// suffix determines db name suffix
export const sites = siteConfigs;

export const port = process.env.PORT || 3000;
export const host = process.env.WEBSITE_HOSTNAME || `localhost:${port}`;
//
// export const analytics = {
//   google: {
//     trackingId: process.env.GOOGLE_TRACKING_ID, // UA-XXXXX-X
//   },
// };

export const googleAPIKey = 'AIzaSyB15RD6TNCoUWbTg6qcICkJcrZguLx77X0';

export const meQuery = `{
  me {
    session {
      localeId siteId
    }
    pointsBalance
    user {
      _id email username firstname lastname states salon {
        _id n
      } group {
        _id n options modules
      } timezone avatar
      { media
        { _id }
      }
      data {
        dailyrate prepdailyrate hourlyrate
      }
    }
    cart {
      _id items {
        _id qty product {
          ... on Reward { _id n type }
        }
      }
    }
    wishlist {
      _id items {
        _id product {
          ... on Reward { _id n type }
        }
      }
    }
    unreadMessages {
      page pages total docs {
        _id subject message dc
        from {
          user {
            firstname lastname avatar { media { _id }}
          }
        }
      }
    }
  }
}`;

export const sidebar = {
  loyalty: { ...beautyerpSidebar, to: ['loyalty-index'] },
  // clubskpLoggedIn: clubskpLoggedInSidebar,
  // clubskpLoggedInSalon: clubskpLoggedInSalonSidebar,
  // messenger: messengerSidebar,
  // advisornet: advisornetSidebar,
  // new
  cmdctrl: cmdctrlSidebar,
  etl: { ...beautyerpSidebar, to: ['etl-index'] },
  filevault: { ...beautyerpSidebar, to: ['filevault-index'] },
  // poe: { ...beautyerpSidebar, to: ['poe-index'] },
  salondb: { ...beautyerpSidebar, to: ['salondb-index'] },
  mytracker: { ...beautyerpSidebar, to: ['mytracker-index'] },
};



// WEBPACK FOOTER //
// ./src/configPublic.js